import { useState, useEffect } from "react";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { ANSWER_OPTIONS } from "../../data/quizData";

export const StageQuestion = ({ 
  stage, 
  currentStageIndex, 
  totalStages, 
  onComplete, 
  onBack,
  initialAnswers = {}
}) => {
  const [answers, setAnswers] = useState(initialAnswers);
  
  // Reset answers when stage changes
  useEffect(() => {
    setAnswers(initialAnswers);
  }, [stage.number, initialAnswers]);

  const handleAnswer = (statementIndex, value) => {
    setAnswers(prev => ({
      ...prev,
      [statementIndex]: value
    }));
  };

  const isComplete = stage.statements.every((_, idx) => answers[idx] !== undefined);
  
  const calculateStageScore = () => {
    return Object.values(answers).reduce((sum, val) => sum + val, 0);
  };

  const handleContinue = () => {
    if (isComplete) {
      onComplete({
        stageNumber: stage.number,
        stageName: stage.name,
        score: calculateStageScore(),
        maxScore: stage.statements.length * 5,
        answers: { ...answers }
      });
    }
  };

  const progress = ((currentStageIndex) / totalStages) * 100;

  return (
    <div className="min-h-screen px-4 py-8 md:py-12">
      <div className="max-w-3xl mx-auto">
        {/* Progress Bar */}
        <div className="mb-8 animate-fadeIn">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-400">
              Stage {currentStageIndex + 1} of {totalStages}
            </span>
            <span className="text-sm text-cyan-400 font-mono">
              {Math.round(progress)}% Complete
            </span>
          </div>
          <div className="score-bar-container">
            <div 
              className="score-bar-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Stage Header */}
        <div className="mb-8 animate-fadeIn delay-100">
          <div className="flex items-center gap-3 mb-3">
            <span className="px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-400 text-sm font-mono border border-cyan-500/20">
              Stage {stage.number}
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 tracking-tight">
            {stage.name}
          </h2>
          <p className="text-slate-400 text-lg">{stage.definition}</p>
        </div>

        {/* Divider */}
        <div className="divider-glow mb-8" />

        {/* Questions */}
        <div className="space-y-6 animate-slideInUp delay-200">
          {stage.statements.map((statement, idx) => (
            <div key={idx} className="stage-card">
              <p className="text-white font-medium text-lg mb-5">{statement}</p>
              
              <div className="grid grid-cols-5 gap-2">
                {ANSWER_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleAnswer(idx, option.value)}
                    className={`flex flex-col items-center justify-center p-3 rounded-lg border transition-colors duration-200 ${
                      answers[idx] === option.value
                        ? "bg-cyan-500/20 border-cyan-500/60 text-cyan-300"
                        : "bg-slate-900/40 border-slate-700 text-slate-400 hover:border-slate-600 hover:bg-slate-900/60"
                    }`}
                    data-testid={`answer-${idx}-${option.value}`}
                  >
                    <span className="text-lg font-bold font-mono mb-1">{option.value}</span>
                    <span className="text-xs text-center leading-tight hidden md:block">
                      {option.label.split(" ")[0]}
                    </span>
                  </button>
                ))}
              </div>
              
              {/* Mobile labels */}
              <div className="flex justify-between mt-2 md:hidden">
                <span className="text-xs text-slate-500">Strongly Disagree</span>
                <span className="text-xs text-slate-500">Strongly Agree</span>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-10 animate-fadeIn delay-300">
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-6 py-3 rounded-lg bg-slate-800/50 border border-slate-700 text-slate-300 hover:bg-slate-800 hover:border-slate-600 transition-colors duration-200"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          <button
            onClick={handleContinue}
            disabled={!isComplete}
            className={`cta-button ${
              !isComplete ? "opacity-50 cursor-not-allowed" : ""
            }`}
            data-testid="continue-btn"
          >
            {currentStageIndex === totalStages - 1 ? "Complete Audit" : "Next Stage"}
            <ArrowRight className="ml-2 w-5 h-5" />
          </button>
        </div>

        {/* Answer scale legend - desktop */}
        <div className="hidden md:flex justify-center gap-6 mt-8 text-sm text-slate-500">
          {ANSWER_OPTIONS.map((option) => (
            <span key={option.value}>
              <span className="font-mono text-slate-400">{option.value}</span> = {option.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};
